
import {AfterViewInit, Inject, Injectable, Injector, Input} from "@angular/core";
import {IWidgetConfig} from "@wm/components-base";
import {find, isArray, isEmpty, isEqual, isNull, isObject, isUndefined, startsWith, toString} from "lodash-es";
import {
  convertDataToObject,
  getOrderedDataset,
  transformDataWithKeys,
  transformFormData,
  getUniqObjsByDataField,
  isEqualWithFields,
  extractDataAsArray,
  setListClass
} from "../formWidgetUtils";
import {Parent, StylableComponent} from "@wm/components-base";
import {isDefined} from "@wm/core";
import {toBoolean} from "@wm/core";

const ALLFIELDS = 'All Fields';
const defaultOptions = 'Option 1, Option 2, Option 3';

export class DataSetItem {
  key: any;
  label: any;
  value: any;
  dataObject?: {[key:string]: any};
  index?: number;
  imgSrc?: string;
  selected?: boolean;
}


@Injectable()
export class DatasetAwareComponent extends StylableComponent implements AfterViewInit{
  @Input() datafield: string = '';
  @Input() displayfield: string = '';
  public dataset: any;
  public datasetItems: DataSetItem[] = [];
  public orderby: string;
  public usekeys!: boolean;
  public displayexpression!: string;
  public displaylabel!: string;
  public displayimagesrc!: string;
  private _modelByValue: any;
  public multiple: any;
  public allowempty!: boolean;
  protected _modelByKey: any;
  public compareby!: string;
  public acceptsArray!: Boolean;
  public content: string;
  public groupedData: any[];
  public collapsible: boolean;
  public layout = '';
  public groupby: string;
  public disabled: boolean;
  public itemsperrow: string;
  public itemsPerRowClass: string = '';
  public readonly: boolean;
  public tabindex: number;
  public itemclass: string = '';
  public showcount: any;
  public binddataset: string;
  constructor(inj: Injector,@Inject(Object) WIDGET_CONFIG: IWidgetConfig, viewParent?: Parent) {
    super(inj, WIDGET_CONFIG, viewParent);
  }

  override ngAfterViewInit() {
    this.initDatasetItems();
  }

  get modelByKey(): any {
    return this._modelByKey;
  }

  set modelByKey(val: any) {
    this.selectByKey(val);
  }

  get datavalue() {
    return this._modelByValue;
  }

  @Input() set datavalue(val: any) {
    if (this.multiple) {
      val = extractDataAsArray(val);
    }
    this._modelByValue = val;

    this.selectByValue(val);
  }

  protected override onPropertyChange(key: string, nv: any, ov?: any) {
    if (key === 'tabindex') {
      return;
    }
    if (key === 'dataset' || key === 'displayfield' || key === 'datafield'
      || key === 'orderby' || key === 'displayexpression' || key === 'usekeys') {
      this.initDatasetItems();
    }
    if (key === 'itemsperrow') {
      setListClass(this);
    }
    super.onPropertyChange(key, nv, ov);
  }

  protected initDatasetItems() {
    let _dataset;

    if (startsWith(this.binddataset || this.dataset,"bind:")) {
      _dataset = this.dataset || defaultOptions;
    } else if (isUndefined(this.dataset)) {
      _dataset = defaultOptions
    } else {
      _dataset = this.dataset;
    }
    if (!_dataset || isEmpty(_dataset)) {
      this.datasetItems = [];
      return;
    }
    const orderedDataset = getOrderedDataset(convertDataToObject(_dataset), this.orderby);

    if (this.usekeys) {
      this.datasetItems = transformDataWithKeys(orderedDataset);
    } else {
      const displayOption = transformFormData({}, orderedDataset, this.datafield, {
        displayField: this.displayfield || this.displaylabel,
        displayExpr: this.displayexpression,
        displayImgSrc: this.displayimagesrc
      });
      // @ts-ignore
      this.datasetItems = [...getUniqObjsByDataField(displayOption, this.datafield, this.displayfield || this.displaylabel, toBoolean(this.allowempty))];
    }
    this.postDatasetItemsInit();
  }

  protected postDatasetItemsInit() {
    this.selectByValue(this.datavalue);
  }

  protected selectByValue(values: Array<any> | any) {
    this.resetDatasetItems();

    // if datavalue is not defined or empty then set the model as undefined.
    if (!isDefined(values) || values === '' || isNull(values) || (values instanceof Array && !values.length)) {
      this._modelByKey = undefined;
      // do not return when allowempty is set to true.
      if (!this.allowempty || !isDefined(values)) {
        return;
      }
    }

    if (this.acceptsArray && !isArray(values)) {
      values = this.allowempty ? [values] : extractDataAsArray(values);
    }

    const filterField = this.datafield === ALLFIELDS ? 'dataObject' : 'key';

    if (isArray(values)) {
      this._modelByKey = [];
      values.forEach(val => {
        const itemByValue = find(this.datasetItems, item => {
          if (filterField === 'dataObject') {
            if (this.compareby && this.compareby.length) {
              return isEqualWithFields(item[filterField], val, this.compareby);
            }
          }
          return (isObject(item.value) ? isEqual(item.value, val) : (toString(item.value)).toLowerCase() === (toString(val)).toLowerCase());
        });
        if (itemByValue) {
          itemByValue.selected = true;
          this._modelByKey.push(itemByValue.key);
        }
      });
    } else {
      this._modelByKey = undefined;
      const itemByValue = find(this.datasetItems, item => {
        if (filterField === 'dataObject') {
          if (this.compareby && this.compareby.length) {
            return isEqualWithFields(item[filterField], values, this.compareby);
          }
        }
        return (isObject(item.value) ? isEqual(item.value, values) : (toString(item.value)).toLowerCase() === (toString(values)).toLowerCase());
      });
      if (itemByValue) {
        itemByValue.selected = true;
        this._modelByKey = itemByValue.key;
      }
    }
  }

  protected resetDatasetItems() {
    if (this.datasetItems) {
      this.datasetItems.forEach(item => item.selected = false);
    }
  }

  protected selectByKey(val: any): any {
    this.resetDatasetItems();

    if (!this.datasetItems.length) {
      return;
    }

    if (this.multiple && !isArray(val)) {
      val = [val];
    }

    // Set the _modelByKey to the modified keys.
    this._modelByKey = val;

    if (this.multiple) {
      this._modelByValue = [];
      val.forEach((key: any) => {
        const itemByKey = find(this.datasetItems, item => {
          // not triple equal, as the instance type can be different.
          // only value comparison should be done.
          return toString(item.key) === toString(key);
        });
        if (itemByKey) {
          itemByKey.selected = true;
          this._modelByValue = [...this._modelByValue, itemByKey.value];
        }
      });
    } else {
      this._modelByValue = '';
      const itemByKey = find(this.datasetItems, item => {
        // not triple equal, as the instance type can be different.
        // only value comparison should be done.
        return toString(item.key) === toString(val);
      });
      if (itemByKey) {
        itemByKey.selected = true;
        this._modelByValue = itemByKey.value;
      }
    }
  }

}
